/**
 * Service-worker lifecycle for the guest PWA. The worker itself is generated at
 * build time by `sw-plugin.ts`, which also carries the caching rationale.
 *
 * The one rule this file exists to enforce: a guest must always be able to get
 * back to the deployed build. Everything below is about the update path, not
 * about caching.
 */

const RESET_PARAM = 'sw';
const RESET_VALUE = 'off';
/** A returning guest re-checks for a new build at most this often. */
const UPDATE_INTERVAL_MS = 60_000;

/**
 * Drop every cache, unregister every worker, reload.
 *
 * The recovery path. A guest wedged on a bad build is holding a phone we cannot
 * reach, often not even their own, and "clear your site data" is not an
 * instruction anyone can follow at a table. `?sw=off` on the venue URL is.
 */
export async function resetServiceWorker(): Promise<void> {
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
  } catch {
    // Nothing here is worth failing a page load over.
  }
}

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;

  // `?sw=off` — handled before anything else, and in dev as well as prod.
  if (new URLSearchParams(location.search).get(RESET_PARAM) === RESET_VALUE) {
    void resetServiceWorker().then(() => {
      const url = new URL(location.href);
      url.searchParams.delete(RESET_PARAM);
      location.replace(url.toString());
    });
    return;
  }

  /**
   * Dev never runs a worker. A worker registered from a production build on
   * this same origin would otherwise sit in front of the dev server and serve
   * a stale shell over `pnpm dev`, which reads as "my edits do nothing".
   */
  if (import.meta.env.DEV) {
    void resetServiceWorker();
    return;
  }

  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((registration) => {
        let lastCheck = Date.now();
        /**
         * A guest reopens the app hours later, usually from the home screen.
         * Asking for an update when the tab becomes visible is what makes the
         * next navigation land on the current build; navigations are
         * network-first anyway, so this only pre-warms the new assets.
         */
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState !== 'visible') return;
          if (Date.now() - lastCheck < UPDATE_INTERVAL_MS) return;
          lastCheck = Date.now();
          void registration.update().catch(() => undefined);
        });
      })
      .catch(() => {
        // An unsupported or blocked worker just means no offline shell.
      });
  });
}
